import { Controller } from "@hotwired/stimulus"

// Accordion-style disclosure that lazy-loads AI insights for a mission step.
//
// Behavior:
//   - First open → GET the endpoint once
//   - 200 (cached) → inject the HTML
//   - 202 (generating) → inject the "generating…" state; wait for the server's
//     Turbo Stream broadcast (subscribed at the layout level) to push the
//     completed content into our content target
//
// We do NOT poll. The server pushes via ActionCable + Turbo Streams when the
// background job finishes. A single 60-second safety-net refetch covers the
// rare case where a broadcast was missed (WebSocket drop during the job).
export default class extends Controller {
  static targets = ["button", "panel", "content", "chevron"]
  static values  = { url: String, loaded: Boolean }

  connect() {
    this.loadedValue = false
    this.safetyTimer = null
  }

  disconnect() {
    this.clearSafetyTimer()
  }

  toggle(event) {
    event?.preventDefault()
    this.panelTarget.hidden ? this.open() : this.close()
  }

  open() {
    this.panelTarget.hidden = false
    this.chevronTarget?.classList.add("rotate-180")
    this.buttonTarget.setAttribute("aria-expanded", "true")
    if (!this.loadedValue) this.fetchInsight()
  }

  close() {
    this.panelTarget.hidden = true
    this.chevronTarget?.classList.remove("rotate-180")
    this.buttonTarget.setAttribute("aria-expanded", "false")
  }

  async fetchInsight() {
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
      const response = await fetch(this.urlValue, {
        headers: { "Accept": "application/json", "X-CSRF-Token": csrf || "" },
        credentials: "same-origin"
      })

      const data = await response.json().catch(() => ({}))

      if (response.status === 200) {
        // Cached completed insight — render and we're done.
        this.contentTarget.innerHTML = data.html || ""
        this.loadedValue = true
        this.clearSafetyTimer()
        return
      }

      if (response.status === 202) {
        // Job is in flight. Render the "generating…" state from the same
        // partial the server will push when it's done. Trust the Turbo Stream
        // broadcast to replace this content; schedule one safety refetch in
        // 60s for the unlikely case of a missed broadcast.
        this.contentTarget.innerHTML = data.html || this.fallbackGeneratingHtml()
        this.scheduleSafetyCheck()
        return
      }

      this.renderError(data.error || `Request failed (${response.status}).`)
    } catch (error) {
      this.renderError("Couldn't load the insight. Check your connection and try again.")
      console.error("[insight] fetch failed:", error)
    }
  }

  scheduleSafetyCheck() {
    this.clearSafetyTimer()
    // Cleared automatically once the Turbo Stream broadcast lands — see
    // contentMutated() below.
    this.safetyTimer = setTimeout(() => this.fetchInsight(), 60_000)
  }

  clearSafetyTimer() {
    if (this.safetyTimer) {
      clearTimeout(this.safetyTimer)
      this.safetyTimer = null
    }
  }

  renderError(message) {
    this.contentTarget.innerHTML = `
      <div class="text-sm text-red-600 py-2">
        <i class="fas fa-exclamation-circle mr-1"></i>
        ${this.escapeHtml(message)}
      </div>
    `
  }

  fallbackGeneratingHtml() {
    return `
      <div class="flex items-center gap-2 text-sm text-text-muted py-2">
        <i class="fas fa-circle-notch fa-spin"></i>
        <span>Generating insight in the background… you'll get a notification when it's ready.</span>
      </div>
    `
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
